import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { Role } from 'src/app/components/catalogs/roles/services/role';
import { BaseComponent } from 'src/app/common/base/base.component';
import { CanComponentDeactivate } from 'src/app/common/guards/unsaved-changes.guard';
import { RoleService } from './services/role.service';
import { Subscription } from 'rxjs';
import { RoleRefresh } from './store/role.action';
import {
  ActionPendingCatalog,
  PendingCatalogColor,
  StatusPendingCatalog,
} from 'src/app/common/enumerations/enumerations';

@Component({
  selector: 'app-roles',
  templateUrl: './roles.component.html',
  styleUrls: ['./roles.component.scss'],
})
export class RolesComponent
  extends BaseComponent
  implements OnInit, CanComponentDeactivate {
  loading!: boolean;
  role?: Role | null;
  roles?: Role[];
  rolesFiltered?: Role[];
  rolesSubs!: Subscription;

  constructor(
    protected override injector: Injector,
    private _service: RoleService,
  ) {
    super(injector);
  }

  override ngOnDestroy(): void {
    this.rolesSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  ngOnInit() {
    this.moduleTitle = 'Roles Catalog';
    this.loadRoles();
    this.setGlobalOptions();
  }

  loadRoles() {
    this.rolesSubs = this.store
      .select((state) => state.Roles.data)
      .subscribe((data) => {
        if (data?.length) {
          this.roles = data;
          if (this.role) {
            this.role = this.roles?.find((r) => r.id === this.role?.id) ?? null;
            this.setRoleOptions();
          }
        }
      });
  }

  override currentUserUpdated(): void {
    this.setGlobalOptions();
    if (this.role) this.setRoleOptions();
  }

  selectedRow(role: Role) {
    this.role = role;
    this.setRoleOptions();
  }

  isPendingRole(role?: Role | null): boolean {
    const color = role?.color as any;
    const hasPendingRole = role?.pendingRoles?.some(
      (pendingRole) =>
        pendingRole?.status === StatusPendingCatalog.Pending ||
        (pendingRole?.status as any) === 'Pending',
    );

    return !!(
      hasPendingRole ||
      color === PendingCatalogColor.Modify ||
      color === PendingCatalogColor.Create ||
      color === PendingCatalogColor.ModifyRSS ||
      color === 'Modify' ||
      color === 'Create' ||
      color === 'ModifyRSS'
    );
  }

  getRoleStatusClasses(role?: Role | null) {
    const pendingDelete = this.isPendingDeleteRole(role);
    const pending = this.isPendingRole(role);
    return {
      green: role?.status == 1 && !pending && !pendingDelete,
      gray: role?.status == 0 && !pending && !pendingDelete,
      yellow: pending && !pendingDelete,
      red: pendingDelete,
    };
  }

  isPendingDeleteRole(role?: Role | null): boolean {
    const action = role?.pendingRoles?.at(-1)?.action as any;
    return (
      action === ActionPendingCatalog.Delete ||
      action === 'Delete'
    );
  }

  getPendingRoleSubmitterName(role?: Role | null): string {
    const pendingRole = role?.pendingRoles?.at(-1) as any;
    return (
      pendingRole?.createdByUser?.name ??
      pendingRole?.userCreatedBy?.name ??
      this.getUser(pendingRole?.createdBy)?.name ??
      ''
    );
  }

  setGlobalOptions() {
    this._service?._canModify$?.next(false);
    this._service?._canApprove$?.next(false);
  }

  setRoleOptions() {
    const userId = this.currentUser?.id;
    const pendingRole = this.role?.pendingRoles?.at(-1);
    const hasPendingChanges = this.roleHasPendingChanges();
    const isPendingDelete =
      pendingRole?.action === ActionPendingCatalog.Delete ||
      (pendingRole?.action as any) === 'Delete';

    const hasCatalogPrivilege = this.hasPrivilege(
      this.privilegeEnum.RoleCatalogChanges,
    );

    if (this.role?.status !== 1 && !hasPendingChanges) {
      this._service._canModify$?.next(false);
      this._service._canApprove$?.next(false);
      this._service._canCancel$?.next(false);
      return;
    }

    // Modify
    const isGroupManager =
      this.role?.aplGroupRole?.aplGroup?.aplGroupManagers?.some(
        (m) => m?.userID === userId,
      ) ?? false;

    const isCategoryManager =
      this.role?.aplGroupRole?.aplGroup?.aplGroupMaster?.aplCategoryMaster?.aplCategory?.aplCategoryManagers?.some(
        (m) => m?.userID === userId,
      ) ?? false;

    const isPTC =
      this.role?.aplGroupRole?.aplGroup?.aplProcedureTrainingCoordinators?.some(
        (p) => p?.userID === userId,
      ) ?? false;

    const isOnlyPTC =
      !hasCatalogPrivilege && !isGroupManager && !isCategoryManager && isPTC;

    const isOwner =
      pendingRole?.createdBy === userId ||
      ((this.role?.id ?? 0) < 0 && this.role?.createdBy === userId);

    const canModify =
      !isPendingDelete &&
      (hasCatalogPrivilege ||
        isGroupManager ||
        isCategoryManager ||
        isOnlyPTC) &&
      (!hasPendingChanges || isOwner);

    this._service._canModify$?.next(canModify);

    // Approve
    const hasApprovePrivilege = this.hasPrivilege(
      this.privilegeEnum.RoleCatalogApprove,
    );

    const canApprove =
      hasApprovePrivilege && !isOwner && hasPendingChanges && !isOnlyPTC;

    this._service._canApprove$?.next(canApprove);

    // Cancel
    const canCancel = isOwner && hasPendingChanges && (canModify || isPendingDelete);
    this._service._canCancel$?.next(canCancel);
  }

  private roleHasPendingChanges(): boolean {
    return !!(
      (this.role?.id ?? 0) < 0 ||
      this.role?.pendingRoles?.some(
        (pendingRole) =>
          pendingRole?.status === StatusPendingCatalog.Pending ||
          (pendingRole?.status as any) === 'Pending',
      )
    );
  }

  applyFilter(e: any) { }
}
